import React, {useContext, useEffect} from 'react';
import {TableBodyProps} from '@src/@types/components/List';
import {IOrderPaymentModel} from '@src/@interfaces/models/Order/IOrderPaymentModel';
import {FormFieldOption} from '@src/@types/components/Form';
import {IPaymentTypeAdditionalFieldModel} from '@src/@interfaces/models/PaymentType/IPaymentTypeAdditionalFieldModel';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import classnames from '@src/helpers/classnames';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import RequiredFieldSign from '@src/components/Form/RequiredFieldSign';
import FormErrorMessage from '@src/components/Form/FormErrorMessage';

export type FieldProps = Parameters<Required<TableBodyProps<IOrderPaymentModel>>['renderRow']>[0] & {
    isDisabled?: boolean,
    isReadonly?: boolean,
    modelOptions?: Record<string, FormFieldOption[]>,
    selectedPaymentType: {
        id: string | number,
        label: FormFieldOption['label'],
        value: FormFieldOption['value'],
        additional_fields: (Required<Pick<
                IPaymentTypeAdditionalFieldModel,
                'payment_type_id' | 'type' | 'label' | 'is_required'
        >> & { id: string | number })[],
    }
};

export default function Row(props: FieldProps) {
    const [design] = useContext(FormFieldDesignContext);

    const {control, setValue} = useFormContext();
    const namePrefix = `payments.${props.rowIndex}.fields`;
    const fields = useWatch({control, name: `${namePrefix}`}) as undefined | {
        id: string | number | null,
        field_id: string | number | null,
        value: string | number | null,
    }[];

    const {id: paymentTypeId, additional_fields: additionalFields} = props.selectedPaymentType;
    useEffect(() => {
        const actualFields = fields?.filter(item => {
            return item.field_id !== paymentTypeId || !additionalFields.find(field => field.id === item.field_id);
        }) ?? [];

        const newFields = additionalFields.reduce<NonNullable<typeof fields>>((res, field) => {
            const existedField = actualFields.find(item => field.id === item.field_id);

            res.push(existedField ?? {
                id: null,
                value: '',
                field_id: field.id,
            });

            return res;
        }, []);
        if (JSON.stringify(newFields) !== JSON.stringify(fields)) {
            setValue(namePrefix, newFields);
        }
    }, [fields, paymentTypeId, additionalFields, setValue, namePrefix]);

    return <div className="row">
        {props.selectedPaymentType.additional_fields.map((item, index) => {
            return <Controller
                    key={`${props.rowIndex}-${item.payment_type_id}-${item.id}`}
                    control={control}
                    name={`${namePrefix}.${index}.value`}
                    render={({field, fieldState: {error, invalid}}) => {
                        return <div className="col-auto">
                            <label
                                    htmlFor={field.name}
                                    className={classnames('col-form-label', {
                                        [`col-form-label-${design.size}`]: design.size,
                                    })}
                            >
                                <RequiredFieldSign displaySign={item.is_required}>{item.label}</RequiredFieldSign>
                            </label>

                            <input
                                    {...field}
                                    value={field.value ?? ''}
                                    id={field.name}
                                    type="text"
                                    className={classnames('form-control', {
                                        [`form-control-${design.size}`]: design.size,
                                        'is-invalid': invalid || Boolean(error?.message)
                                    })}
                                    readOnly={design.fieldsAreReadOnly}
                                    disabled={design.fieldsAreDisabled}
                                    placeholder={item.label}
                            />

                            <FormErrorMessage fieldName={field.name} forceDisplaying/>
                        </div>;
                    }}
            />;
        })}
    </div>;
}
